import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getInvoice } from '../../api/invoices'
import { DetailSection } from '@/components/DetailSection'
import { PageHeader } from '@/components/PageHeader'
import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { FileText } from 'lucide-react'

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()

  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id!),
    enabled: !!id,
  })

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Lade Rechnung…</div>
  if (isError || !invoice) return (
    <div className="p-6">
      <p className="text-sm text-destructive">Rechnung nicht gefunden.</p>
    </div>
  )

  return (
    <div className="p-6 max-w-2xl">
      <PageHeader
        title={invoice.number ?? `Rechnung #${invoice.id}`}
        breadcrumb={[{ label: 'Rechnungen', href: '/invoices' }]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <a href={`/api/invoice/${id}/pdf/`} target="_blank" rel="noreferrer">
              <FileText className="mr-1.5 size-4" />
              PDF herunterladen
            </a>
          </Button>
        }
      />
      <div className="mt-4 space-y-4">
        <DetailSection
          rows={[
            { label: 'Rechnungsnummer', value: invoice.number, copyable: true },
            { label: 'Status', value: <StatusBadge status={invoice.status} /> },
            { label: 'Betrag', value: invoice.total === null ? '—' : `${Number(invoice.total).toFixed(2)} €` },
            { label: 'Währung', value: invoice.currency_id },
            {
              label: 'Rechnungsdatum',
              value: invoice.date
                ? new Date(invoice.date).toLocaleDateString('de-DE')
                : undefined,
            },
            { label: 'Erstellt', value: new Date(invoice.created_at).toLocaleString('de-DE') },
          ].filter((r) => r.value !== undefined && r.value !== null)}
        />
        <div className="text-sm">
          <span className="text-muted-foreground">Auftrag: </span>
          <span className="font-mono">{invoice.order_id === null ? '—' : `#${invoice.order_id}`}</span>
        </div>
      </div>
    </div>
  )
}
